import { useEffect, useMemo, useState } from 'react'

import { useGlobalCopyStore } from '../../common'
import type { Beat } from '../../core'
import type { BeatActions, InsightRating } from '../beatActions'
import type { DismissType } from '../components/DismissInsightDialog'
import type { FeedbackDialogCloseReason } from '../components/FeedbackDialog'

export type FeedbackVariant = InsightRating | 'hide'
export type CardState = 'visible' | 'confirming' | 'dismissed'

/**
 * Manages feedback and dismiss UI state for a beat card.
 * Handles feedback bar, dismiss dialog, confirmation card, and survey dialogs.
 */
export const useBeatFeedbackFlow = (beat: Beat, beatActions: BeatActions) => {
  const copyStore = useGlobalCopyStore()

  const [feedbackRating, setFeedbackRating] = useState<InsightRating | null>(null)
  const [feedbackVariant, setFeedbackVariant] = useState<FeedbackVariant | null>(null)
  const [isDismissOpen, setIsDismissOpen] = useState(false)
  const [hasClosedConfirmation, setHasClosedConfirmation] = useState(false)

  const insights_payload = beat.insights_payload

  // Reset the confirmation-closed flag whenever the beat is no longer dismissed
  // (undo or a host reload flipping is_dismissed), so a fresh dismiss always
  // starts at the confirmation card instead of jumping to 'dismissed'.
  useEffect(() => {
    if (!insights_payload?.is_dismissed) setHasClosedConfirmation(false)
  }, [insights_payload?.is_dismissed])

  const cardState: CardState = useMemo(() => {
    if (insights_payload?.is_dismissed && hasClosedConfirmation) return 'dismissed'
    if (insights_payload?.is_dismissed) return 'confirming'
    return 'visible'
  }, [insights_payload?.is_dismissed, hasClosedConfirmation])

  const resolveSurvey = () => {
    const feedbackCopy = copyStore.insights_feed?.feedback
    const hideCopy = copyStore.insights_feed?.hide_feedback

    const chrome = {
      characterLimitLabel: (feedbackVariant === 'hide' ? hideCopy : feedbackCopy)?.character_limit,
      closeAriaLabel: (feedbackVariant === 'hide' ? hideCopy : feedbackCopy)?.close_aria ?? 'Close',
      submitText: (feedbackVariant === 'hide' ? hideCopy : feedbackCopy)?.submit ?? 'Submit',
      backText: (feedbackVariant === 'hide' ? hideCopy : feedbackCopy)?.back ?? 'Back',
    }

    if (feedbackVariant === 'hide' && hideCopy) {
      return {
        title: hideCopy.title,
        options: Object.values(hideCopy.options),
        ...chrome,
      }
    }

    const options =
      feedbackVariant === 'not_helpful'
        ? Object.values(feedbackCopy?.not_helpful_options ?? {})
        : Object.values(feedbackCopy?.helpful_options ?? {})

    return {
      title: feedbackCopy?.tell_us_more_title ?? 'Tell us more',
      options,
      ...chrome,
    }
  }

  const survey = resolveSurvey()

  const wrappedActions = useMemo<BeatActions>(() => {
    const menu = beatActions.onMenuAction
    if (!menu) return beatActions
    return {
      ...beatActions,
      onMenuAction: {
        ...menu,
        onAction: (action) => {
          if (action === 'helpful' || action === 'not_helpful') {
            setFeedbackRating(action)
          } else if (action === 'hide') {
            setIsDismissOpen(true)
          }
          menu.onAction(action)
        },
      },
    }
  }, [beatActions])

  const openRatingSurvey = () => {
    if (feedbackRating) setFeedbackVariant(feedbackRating)
  }

  const openHideSurvey = () => setFeedbackVariant('hide')

  const closeSurvey = (reason?: FeedbackDialogCloseReason) => {
    if (reason) beatActions.onFeedbackDialogClose?.(reason)
    if (feedbackVariant !== 'hide') setFeedbackRating(null)
    setFeedbackVariant(null)
  }

  const submitSurvey = (option: string, detail?: string) => {
    if (!feedbackVariant || !insights_payload) return
    beatActions.onSubmitFeedback?.({
      beatGuid: insights_payload.guid,
      source: feedbackVariant,
      option,
      detail,
    })
    setFeedbackVariant(null)
    if (feedbackVariant === 'hide') {
      setHasClosedConfirmation(true)
    } else {
      setFeedbackRating(null)
    }
  }

  const confirmDismiss = (type: DismissType) => {
    if (!insights_payload) return
    beatActions.onDismissInsight?.({ beatGuid: insights_payload.guid, type })
  }

  const undoDismiss = () => {
    if (!insights_payload) return
    beatActions.onUndoDismiss?.({ beatGuid: insights_payload.guid })
    setFeedbackRating(null)
  }

  const collapseConfirmation = () => {
    setHasClosedConfirmation(true)
  }

  const closeDismissDialog = () => setIsDismissOpen(false)

  return {
    cardState,
    isDismissOpen,
    isSurveyOpen: feedbackVariant !== null,
    isFeedbackBarShown: feedbackRating !== null,
    survey,
    wrappedActions,
    openRatingSurvey,
    openHideSurvey,
    closeSurvey,
    submitSurvey,
    confirmDismiss,
    undoDismiss,
    collapseConfirmation,
    closeDismissDialog,
    feedbackDialogCallbacks: {
      onBackClick: beatActions.onFeedbackDialogBack,
      onTextInputFocus: beatActions.onFeedbackTextFocus,
    },
  }
}
